import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

const LABELS = { regione: "Regione", spin: "Sede SPIN", provincia: "Provincia", settore: "Settore", dipendenti: "Fascia dipendenti", fatturato: "Fascia di fatturato", livello: "Livello", tipo: "Tipo evento" };
export const ALL_FILTERS = ["regione", "spin", "provincia", "settore", "dipendenti", "fatturato", "livello"];
export const SEDE_FILTERS = ["settore", "dipendenti", "fatturato", "livello"];
const REMOVED = "Altro (opzione rimossa)";

export const MultiCheck = ({ name, options, selected, onChange }) => (
    <details className="relative">
        <summary className={`edi-input cursor-pointer list-none flex items-center justify-between gap-2 ${selected.length ? "surface-soft !bg-edi-lime-soft border-transparent font-semibold shadow-[inset_0_-3px_0_var(--edi-lime)]" : ""}`} data-testid={`filter-${name}`}>
            <span>{LABELS[name]}</span>
            <span className="text-sm text-edi-muted">{selected.length ? `${selected.length} selezionati` : "Tutti"}</span>
        </summary>
        <fieldset className="absolute z-30 mt-1 w-72 max-w-[85vw] max-h-72 overflow-y-auto edi-card p-3 shadow-md">
            <legend className="sr-only">{LABELS[name]}</legend>
            {options.map((o) => (
                <label key={o.value} className="flex items-start gap-2 py-1">
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--edi-primary)]" checked={selected.includes(o.value)} data-testid={`filter-${name}-${o.value}`}
                        onChange={() => onChange(selected.includes(o.value) ? selected.filter((v) => v !== o.value) : [...selected, o.value])} />
                    <span>{o.label}</span>
                </label>
            ))}
        </fieldset>
    </details>
);

export const useFilterOptions = () => {
    const [opts, setOpts] = useState(null);
    useEffect(() => { api.get("/admin/filter-options").then(({ data }) => setOpts(data)); }, []);
    return opts;
};

export const FilterBar = ({ keys, period = true, version = true }) => {
    const [params, setParams] = useSearchParams();
    const opts = useFilterOptions();
    const [from, setFrom] = useState(params.get("from") || "");
    const [to, setTo] = useState(params.get("to") || "");
    const [error, setError] = useState("");
    if (!opts) return null;
    const optionsFor = (k) => {
        if (k === "spin") return opts.spin.map((s) => ({ value: s.id, label: s.name }));
        if (k === "provincia") return opts.provincia.map((p) => ({ value: p.sigla, label: `${p.name} (${p.sigla})` }));
        const list = (opts[k] || []).map((v) => ({ value: v, label: v }));
        return ["settore", "dipendenti", "fatturato"].includes(k) ? [...list, { value: REMOVED, label: REMOVED }] : list;
    };
    const update = (mutate) => {
        const next = new URLSearchParams(params);
        mutate(next);
        next.delete("page");
        setParams(next);
    };
    const setList = (k, values) => update((n) => { n.delete(k); values.forEach((v) => n.append(k, v)); });
    const applyPeriod = (f, t) => {
        if (f && t && f > t) return setError("La data iniziale deve precedere quella finale");
        setError("");
        return update((n) => { f ? n.set("from", f) : n.delete("from"); t ? n.set("to", t) : n.delete("to"); });
    };
    const reset = () => { setFrom(""); setTo(""); setError(""); const n = new URLSearchParams(); if (params.get("q")) n.set("q", params.get("q")); setParams(n); };

    return (
        <section aria-label="Filtri" className="edi-card p-4 mb-6" data-testid="filter-bar">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {keys.map((k) => <MultiCheck key={k} name={k} options={optionsFor(k)} selected={params.getAll(k)} onChange={(v) => setList(k, v)} />)}
                {version && (
                    <div>
                        <label htmlFor="filter-version" className="sr-only">Versione questionario</label>
                        <select id="filter-version" className="edi-input" value={params.get("version") || ""} data-testid="filter-version"
                            onChange={(e) => update((n) => (e.target.value ? n.set("version", e.target.value) : n.delete("version")))}>
                            <option value="">Versione questionario: attiva</option>
                            {opts.versions.map((v) => <option key={v.version} value={v.version}>{`Versione ${v.version}${v.active ? " (attiva)" : ""}`}</option>)}
                        </select>
                    </div>
                )}
            </div>
            {period && (
                <fieldset className="mt-3 flex flex-wrap items-end gap-3">
                    <legend className="font-medium mb-1">Periodo (data di compilazione)</legend>
                    <div><label htmlFor="filter-from" className="edi-help block">Dal</label><input id="filter-from" type="date" className="edi-input" value={from} onChange={(e) => setFrom(e.target.value)} aria-invalid={error ? "true" : "false"} aria-describedby={error ? "period-error" : undefined} data-testid="filter-from" /></div>
                    <div><label htmlFor="filter-to" className="edi-help block">Al</label><input id="filter-to" type="date" className="edi-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="filter-to" /></div>
                    <button type="button" className="btn-secondary py-2" onClick={() => applyPeriod(from, to)} data-testid="filter-period-apply">Applica periodo</button>
                </fieldset>
            )}
            {error && <p id="period-error" role="alert" className="edi-error" data-testid="filter-period-error">{error}</p>}
            <button type="button" className="btn-ghost mt-3 underline" onClick={reset} data-testid="filter-reset">Azzera filtri</button>
        </section>
    );
};
